import classNames from 'classnames';
import { FC, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  ActivityListDataFragment,
  useAddActivityToProviderCollectionMutation,
  useRemoveActivityFromProviderCollectionMutation,
} from '../../../../generated/graphql';
import {
  AnalyticsComponent,
  AnalyticsPage,
  CommonAnalyticsEvent,
  trackProviderEvent,
} from '../../../../lib/analytics';

import { RefetchProviderActivities } from '../../../types/activity';
import {
  ProviderCollection,
  ProviderCollections,
} from '../../../types/providerCollection';
import { ColumnHeaderType, ColumnOrder } from '../../../types/tables';

import {
  ProviderActivityObject,
  getProviderActivitiesObject,
  getProviderActivityData,
} from '../../../lib/providerActivity';

import CloseX from '../../../svgs/CloseX';
import IllustrationBook from '../../../svgs/IllustrationBook';

import Button from '../../../components/Button';
import TableHeader from '../../../components/TableHeader';

import {
  PROVIDER_ACTIVITY_ORDERS,
  columnHeaders,
  sortOptions,
} from '../helpers';

import { ApolloError } from '@apollo/client';
import {
  ChevronRightIcon,
  ClipboardListIcon,
  CollectionIcon,
  DotsHorizontalIcon,
  HomeIcon,
} from '@heroicons/react/outline';
import toast from 'react-hot-toast';
import { HomecomingError, matchHomecomingError } from '../../../../lib/errors';
import { ContextMenuPosition } from '../../../types/contextMenu';
import { AddToLibraryMenuProps } from '../../AddToLibraryMenu';
import ToastAlert from '../../ToastAlert';
import ActivityActionsMenu from './ActivityActionsMenu';
import LibraryTableRow from './LibraryTableRow';
import ProviderCollectionTableRow from './LibraryTableRow/ProviderCollectionTableRow';
import ProviderCollectionMenu from './ProviderCollectionMenu';
import { isStandardAssessment } from '../../../lib/assessments';
import { ContentType } from '../../../lib/followUp';

const LibraryTable: FC<{
  activityOrder: ColumnOrder;
  hasEmptyProviderCollection: boolean;
  providerActivities: ActivityListDataFragment[] | undefined;
  selectedProviderCollection: ProviderCollection | undefined;
  setActivityOrder: React.Dispatch<React.SetStateAction<ColumnOrder>>;
  setSelectedProviderActivity: React.Dispatch<
    React.SetStateAction<ActivityListDataFragment | undefined>
  >;
  setSelectedProviderCollection: React.Dispatch<
    React.SetStateAction<ProviderCollection | undefined>
  >;
  refetchProviderActivities: RefetchProviderActivities;
  clearSearchAndFilters: () => void;
  showEmptyTableFilterState: boolean;
  allProviderCollections: ProviderCollections | undefined;
  sortedProviderCollections: ProviderCollections | undefined;
  duplicateActivity?: () => void;
  renameProviderCollection?: () => void;
  setIsConfirmDeleteCollectionModalOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsConfirmArchiveActivityModalOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsRenameActivityModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFileSettingsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsProviderActivitySlideoverOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  createProviderCollection?: (
    addSelectedProviderActivityToCollection?: boolean,
  ) => void;
  openAddOrEditTextModal?: () => void;
  followUpMode?: boolean;
  restrictSingleSelection?: boolean;
  setSelectedProviderActivitiesObject?: React.Dispatch<
    React.SetStateAction<ProviderActivityObject>
  >;
  selectedProviderActivitiesObject?: ProviderActivityObject;
  selectedProviderActivitiesList?: ActivityListDataFragment[];
  selectedProviderActivity?: ActivityListDataFragment;
  showActivityActionsMenu?: boolean;
  setShowActivityActionsMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  sendProviderActivitiesInFollowUp?: (
    providerActivities: ActivityListDataFragment[],
  ) => void;
  contentTypeFilter: ContentType | null;
  searchTerm: string | null;
  BoundAddToLibrary?: FC<AddToLibraryMenuProps>;
  allProviderActivities: ActivityListDataFragment[] | undefined;
  resourceName?: string;
}> = ({
  activityOrder,
  hasEmptyProviderCollection,
  providerActivities,
  showEmptyTableFilterState,
  selectedProviderCollection,
  setActivityOrder,
  duplicateActivity,
  renameProviderCollection,
  setSelectedProviderActivity,
  setIsProviderActivitySlideoverOpen,
  setSelectedProviderCollection,
  setIsConfirmArchiveActivityModalOpen,
  setIsRenameActivityModalOpen,
  setIsFileSettingsModalOpen,
  setIsConfirmDeleteCollectionModalOpen,
  clearSearchAndFilters,
  refetchProviderActivities,
  createProviderCollection,
  openAddOrEditTextModal,
  setSelectedProviderActivitiesObject,
  setShowActivityActionsMenu,
  sortedProviderCollections,
  allProviderCollections,
  followUpMode = false,
  restrictSingleSelection = false,
  selectedProviderActivitiesObject,
  selectedProviderActivitiesList,
  selectedProviderActivity,
  showActivityActionsMenu,
  sendProviderActivitiesInFollowUp,
  contentTypeFilter,
  searchTerm,
  BoundAddToLibrary,
  allProviderActivities,
  resourceName = 'resource',
}) => {
  const columns = columnHeaders.activity;

  const [previousColumnClicked, setPreviousColumnClicked] =
    useState<ColumnHeaderType | null>();

  const [activityActionsMenuPosition, setActivityActionsMenuPosition] =
    useState<ContextMenuPosition>({ x: 0, y: 0 });

  const [removeActivityFromProviderCollectionMutation] =
    useRemoveActivityFromProviderCollectionMutation();

  const [addActivityToProviderCollectionMutation] =
    useAddActivityToProviderCollectionMutation();

  const globalCheckboxRef =
    useRef() as React.MutableRefObject<HTMLInputElement>;

  const [isProcessingDrag, setIsProcessingDrag] = useState(false);

  const [lastCheckedIndex, setLastCheckedIndex] = useState<number | null>(null);

  const handleHeaderClick = (column: ColumnHeaderType) => {
    function getNextActivityOrder(
      activityOrder: ColumnOrder | null,
      orderType: 'activity' | 'dateAdded',
    ) {
      const nextOrderIndex =
        PROVIDER_ACTIVITY_ORDERS[orderType].findIndex(
          (order) => order === activityOrder,
        ) + 1;

      if (PROVIDER_ACTIVITY_ORDERS[orderType][nextOrderIndex])
        return PROVIDER_ACTIVITY_ORDERS[orderType][nextOrderIndex];
      return PROVIDER_ACTIVITY_ORDERS[orderType][0];
    }

    if (column.field === 'activity' || column.field === 'dateAdded') {
      const nextActivityOrder = getNextActivityOrder(
        activityOrder,
        column.field,
      );
      setPreviousColumnClicked(column);
      setActivityOrder(nextActivityOrder);
    }
  };

  const emptyTableWithoutSelectedProviderCollection = Boolean(
    showEmptyTableFilterState && !selectedProviderCollection,
  );
  const emptyTableWithSelectedProviderCollection = Boolean(
    showEmptyTableFilterState && selectedProviderCollection,
  );

  const globalCheckboxChecked = Boolean(
    providerActivities?.length &&
      selectedProviderActivitiesList?.length &&
      providerActivities?.length === selectedProviderActivitiesList?.length,
  );

  function toggleAllCheckboxes() {
    setSelectedProviderActivitiesObject?.(
      globalCheckboxChecked
        ? {}
        : getProviderActivitiesObject?.(providerActivities),
    );
  }

  const addActivityToProviderCollection = async (
    providerCollection: ProviderCollection,
    providerActivity: ActivityListDataFragment,
  ) => {
    if (isProcessingDrag || !providerActivity) return;

    try {
      setIsProcessingDrag(true);
      const providerActivityData = getProviderActivityData(providerActivity);

      if (
        providerActivityData.activityId &&
        providerCollection.id &&
        providerActivity.activityType
      ) {
        const addActivityToProviderCollectionInput = {
          activityId: providerActivityData.activityId,
          providerCollectionId: providerCollection.id,
          activityType: providerActivity.activityType,
        };

        const { data } = await addActivityToProviderCollectionMutation({
          variables: {
            input: addActivityToProviderCollectionInput,
          },
        });

        if (data?.addActivityToProviderCollection) {
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              level="success"
              message={`Moved to ${providerCollection.name}.`}
            />
          ));

          await refetchProviderActivities?.();
        }
      }
    } catch (err) {
      if (
        matchHomecomingError(
          err as ApolloError,
          HomecomingError.ResourceForbidden,
        )
      ) {
        const warningMessage = `This resource is already in ${providerCollection.name}.`;
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message={warningMessage}
            level="warning"
          />
        ));
      } else {
        const errorMessage = `Couldn't add resource to ${providerCollection.name}. Please try again later.`;

        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message={errorMessage}
            level="error"
          />
        ));
      }
    } finally {
      setIsProcessingDrag(false);
      setSelectedProviderActivity?.(undefined);
    }
  };

  const removeActivityFromProviderCollection = async (
    selectedProviderActivity?: ActivityListDataFragment,
  ) => {
    if (!selectedProviderActivity) return;

    const providerActivity = getProviderActivityData(selectedProviderActivity);
    const collectionIdToRemove =
      selectedProviderCollection?.id ||
      selectedProviderActivity?.providerCollectionIds?.[0];

    if (!collectionIdToRemove) return;

    const collectionToRemove =
      selectedProviderCollection ||
      allProviderCollections?.find((c) => c.id === collectionIdToRemove);

    if (!collectionToRemove) return;

    const removeActivityFromProviderCollectionInput = {
      activityId: providerActivity?.activityId ?? '',
      providerCollectionId: collectionIdToRemove,
      activityType: selectedProviderActivity?.activityType,
    };

    try {
      const { data } = await removeActivityFromProviderCollectionMutation({
        variables: {
          input: removeActivityFromProviderCollectionInput,
        },
      });

      if (data?.removeActivityFromProviderCollection) {
        await refetchProviderActivities();
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            level="success"
            message={`Removed from ${collectionToRemove.name}.`}
          />
        ));
      }
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={`Couldn't remove ${providerActivity?.activityTitle} from ${collectionToRemove.name}.`}
          level="warning"
        />
      ));
    }
  };

  const handleCheckboxClick = (
    index: number,
    providerActivityId: string,
    isChecked: boolean,
    event: React.MouseEvent<HTMLInputElement>,
  ) => {
    if (
      event.shiftKey &&
      lastCheckedIndex !== null &&
      setSelectedProviderActivitiesObject
    ) {
      // Get the range of items to select
      const start = Math.min(lastCheckedIndex, index);
      const end = Math.max(lastCheckedIndex, index);

      // Create a new object with all the selections
      const newSelectedActivities = { ...selectedProviderActivitiesObject };

      // Select all items in the range
      providerActivities?.slice(start, end + 1).forEach((activity) => {
        const activityId = getProviderActivityData(activity).activityId;
        if (!isChecked) {
          newSelectedActivities[activityId] = activity;
        } else {
          delete newSelectedActivities[activityId];
        }
      });

      setSelectedProviderActivitiesObject(newSelectedActivities);
    } else {
      // Handle single selection
      setSelectedProviderActivitiesObject?.((prev) => {
        if (!isChecked) {
          return { ...prev, [providerActivityId]: providerActivities?.[index] };
        } else {
          const { [providerActivityId]: _, ...rest } = prev;
          return rest;
        }
      });
    }

    // Update the last checked index
    setLastCheckedIndex(index);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-full w-full">
        <div className="h-full overflow-hidden overflow-y-auto rounded-lg border border-neutral-100">
          <div
            className={classNames(
              'sticky top-0 z-[9] flex w-full flex-row items-center justify-between bg-white px-4 transition-all duration-150',
              !selectedProviderCollection && 'min-h-0 opacity-0',
              selectedProviderCollection &&
                'min-h-[57px] border-b border-neutral-75 opacity-100',
            )}
          >
            {selectedProviderCollection && (
              <>
                <div className="flex flex-row items-center text-caption text-neutral-150">
                  <div
                    className="flex cursor-pointer flex-row items-center rounded-md px-3 py-2 hover:bg-neutral-25"
                    onClick={() => setSelectedProviderCollection(undefined)}
                  >
                    {resourceName === 'form' ? (
                      <ClipboardListIcon className="mr-2 h-4 w-4 text-secondary-125" />
                    ) : (
                      <HomeIcon className="mr-2 h-4 w-4 text-secondary-125" />
                    )}
                    <div className="font-medium line-clamp-2">
                      All {resourceName}s
                    </div>
                  </div>
                  <div className="mr-1">
                    <ChevronRightIcon className="ml-1 mr-2 h-[18px] w-[18px] text-neutral-150" />
                  </div>
                  <div className="flex flex-row items-center py-2 px-2">
                    <CollectionIcon className="mr-2 h-[18px] w-[18px] min-w-[18px] text-secondary-150" />
                    <div className="font-medium">
                      {selectedProviderCollection?.name}
                    </div>
                  </div>
                </div>

                {!followUpMode && (
                  <ProviderCollectionMenu
                    onClickRenameCollection={() => {
                      renameProviderCollection?.();
                      trackProviderEvent(CommonAnalyticsEvent.ComponentViewed, {
                        originPage: 'Provider Library',
                        component: `Rename Provider Collection | Provider Library Table`,
                      });
                    }}
                    onClickDeleteCollection={() => {
                      setIsConfirmDeleteCollectionModalOpen?.(true);
                      trackProviderEvent(CommonAnalyticsEvent.ComponentViewed, {
                        originPage: 'Provider Library',
                        component: `Delete Provider Collection | Provider Library Table`,
                      });
                    }}
                    onClickSendToClient={() => {
                      sendProviderActivitiesInFollowUp?.(
                        allProviderActivities?.filter((activity) =>
                          activity.providerCollectionIds.includes(
                            selectedProviderCollection?.id,
                          ),
                        ) ?? [],
                      );
                      trackProviderEvent(CommonAnalyticsEvent.ComponentViewed, {
                        originPage: 'Provider Library',
                        component: `Send Provider Collection | Provider Library Table`,
                      });
                    }}
                  />
                )}
              </>
            )}
          </div>
          {Boolean(
            !showEmptyTableFilterState && !hasEmptyProviderCollection,
          ) && (
            <table className="min-w-full">
              <thead
                className={classNames(
                  'sticky top-0 z-20 h-[40px] w-full border-b border-neutral-100 bg-white',
                  {
                    'top-[57px]': selectedProviderCollection,
                  },
                )}
              >
                <tr className="border-b border-neutral-75 bg-white">
                  <th
                    scope="col"
                    className="w-12 bg-white pl-6 pr-4 sm:w-16 sm:px-8"
                  >
                    {!restrictSingleSelection && (
                      <input
                        ref={globalCheckboxRef}
                        checked={globalCheckboxChecked}
                        type="checkbox"
                        className="h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-2 focus:ring-secondary-125 sm:left-6"
                        onChange={toggleAllCheckboxes}
                      />
                    )}
                  </th>
                  {columns.map((column: ColumnHeaderType, i: number) => {
                    const isActionsColumn = column.field === 'activityActions';
                    return (
                      <TableHeader
                        key={`providerActivitiesTableColumnHeader_${column.field}`}
                        className={classNames(
                          'bg-white text-caption',
                          isActionsColumn && 'min-w-[120px] pr-6',
                        )}
                        column={{
                          ...column,
                          headerName:
                            column.headerName === 'Resource' &&
                            resourceName === 'form'
                              ? 'Form'
                              : column.headerName,
                        }}
                        columnOrder={activityOrder}
                        sortOptions={sortOptions}
                        previousColumnClicked={previousColumnClicked}
                        handleHeaderClick={handleHeaderClick}
                        style={
                          isActionsColumn
                            ? { textAlign: 'right' as const }
                            : undefined
                        }
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody className="mt-[40px] overflow-y-scroll">
                {!selectedProviderCollection &&
                  sortedProviderCollections?.map((collection, idx) => {
                    return (
                      <ProviderCollectionTableRow
                        key={collection.id}
                        providerCollection={collection}
                        setSelectedProviderCollection={
                          setSelectedProviderCollection
                        }
                        followUpMode={followUpMode}
                        sendProviderActivitiesInFollowUp={
                          sendProviderActivitiesInFollowUp
                        }
                        providerActivitiesInCollection={allProviderActivities?.filter(
                          (activity) =>
                            activity.providerCollectionIds.includes(
                              collection.id,
                            ),
                        )}
                        addActivityToProviderCollection={
                          addActivityToProviderCollection
                        }
                      />
                    );
                  })}
                {providerActivities?.map(
                  (providerActivity, providerActivityIndex) => {
                    const providerActivityId =
                      getProviderActivityData(providerActivity).activityId;

                    const foundCheckedProviderActivity =
                      selectedProviderActivitiesObject?.[providerActivityId];

                    return (
                      <LibraryTableRow
                        key={`activityTableRow_${providerActivityIndex}`}
                        followUpMode={followUpMode}
                        restrictSingleSelection={restrictSingleSelection}
                        providerActivity={providerActivity}
                        setSelectedProviderActivity={
                          setSelectedProviderActivity
                        }
                        setIsProviderActivitySlideoverOpen={
                          setIsProviderActivitySlideoverOpen
                        }
                        openAddOrEditTextModal={openAddOrEditTextModal}
                        isLastProviderActivity={
                          providerActivities.length - 1 ===
                          providerActivityIndex
                        }
                        setSelectedProviderActivitiesObject={
                          setSelectedProviderActivitiesObject
                        }
                        selectedProviderActivitiesObject={
                          selectedProviderActivitiesObject
                        }
                        isChecked={Boolean(foundCheckedProviderActivity)}
                        providerActivityId={providerActivityId}
                        showActivityActionsMenu={showActivityActionsMenu}
                        setShowActivityActionsMenu={setShowActivityActionsMenu}
                        setActivityActionsMenuPosition={
                          setActivityActionsMenuPosition
                        }
                        sendProviderActivitiesInFollowUp={
                          sendProviderActivitiesInFollowUp
                        }
                        canDragIntoCollection={
                          !isStandardAssessment(providerActivity) &&
                          !selectedProviderCollection &&
                          !contentTypeFilter &&
                          sortedProviderCollections?.length > 0
                        }
                        onCheckboxClick={(event) =>
                          handleCheckboxClick(
                            providerActivityIndex,
                            providerActivityId,
                            Boolean(foundCheckedProviderActivity),
                            event,
                          )
                        }
                        selectedProviderCollection={selectedProviderCollection}
                      />
                    );
                  },
                )}
              </tbody>
            </table>
          )}
          <div
            className={classNames(
              'flex w-full flex-col items-center justify-start transition-all duration-500',
              !emptyTableWithoutSelectedProviderCollection && 'h-0 opacity-0',
            )}
          >
            {emptyTableWithoutSelectedProviderCollection && (
              <>
                <IllustrationBook className="m-3 mt-12 h-[64px] w-[79px] text-primary-125" />
                <h3 className="mt-1 font-sans text-subtitle-small font-medium text-green-150">
                  Nothing to see here...
                </h3>
                <Button
                  theme="secondary"
                  size="small"
                  className="mt-6"
                  title="Clear search and filters"
                  IconComponent={CloseX}
                  iconClassName="text-neutral-125 h-2.5 w-2.5 mt-0.5"
                  onClick={clearSearchAndFilters}
                />
              </>
            )}
          </div>
          {emptyTableWithSelectedProviderCollection && (
            <div className="mt-6 flex w-full flex-col items-center justify-start">
              <CollectionIcon className="mb-1 mt-6 !h-12 w-full text-secondary-150" />
              <h3 className="mt-1 font-sans text-subtitle-small font-medium text-secondary-150">
                {hasEmptyProviderCollection
                  ? 'This collection is empty'
                  : 'Nothing to see here...'}
              </h3>
              {hasEmptyProviderCollection && (
                <p className="mt-2 w-96 text-center text-body text-neutral-150">
                  {!followUpMode ? (
                    <span className="inline-block items-center text-neutral-150">
                      Add existing resources by right-clicking or clicking the
                      <DotsHorizontalIcon className="ml-1 inline-block h-4 w-4 text-neutral-150" />{' '}
                      icon on an item in
                      <HomeIcon className="ml-1 mb-0.5 inline-block h-4 w-4 text-secondary-125" />{' '}
                      <span className="text-caption font-medium">
                        All resources
                      </span>
                      , or add a new resource using the button below.
                    </span>
                  ) : (
                    <span>
                      Add resources to this collection in the Library.
                    </span>
                  )}
                </p>
              )}
              {hasEmptyProviderCollection ? (
                <div className="mt-3">
                  {BoundAddToLibrary && (
                    <BoundAddToLibrary
                      title="Add to collection"
                      buttonTheme="secondary"
                      iconPosition="left"
                      buttonSize="small"
                      iconClassName="text-secondary-150"
                    />
                  )}
                </div>
              ) : (
                <Button
                  theme="secondary"
                  size="small"
                  className="mt-6"
                  title="Clear search and filters"
                  IconComponent={CloseX}
                  iconClassName="text-neutral-125 h-2.5 w-2.5 mt-0.5"
                  onClick={clearSearchAndFilters}
                />
              )}
            </div>
          )}
        </div>
        {setShowActivityActionsMenu && (
          <ActivityActionsMenu
            setShowActivityActionsMenu={setShowActivityActionsMenu}
            setSelectedProviderActivity={setSelectedProviderActivity}
            isOpen={showActivityActionsMenu}
            setIsOpen={(e) => {
              e.preventDefault();
              if (!followUpMode) {
                if (!showActivityActionsMenu) {
                  setActivityActionsMenuPosition({
                    x: e.pageX,
                    y: e.pageY,
                  });
                }
                setShowActivityActionsMenu(!showActivityActionsMenu);
              }
            }}
            position={activityActionsMenuPosition}
            providerActivity={selectedProviderActivity}
            providerCollections={allProviderCollections}
            selectedProviderCollection={selectedProviderCollection}
            addActivityToProviderCollection={addActivityToProviderCollection}
            onClickRemoveActivityFromProviderCollection={async () => {
              await removeActivityFromProviderCollection(
                selectedProviderActivity,
              );
              trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                originPage: AnalyticsPage.Library,
                originComponent: AnalyticsComponent.ActivityActionsMenu,
                buttonName: 'Remove this from collection',
              });
            }}
            onClickAddActivityToProviderCollection={() => {
              createProviderCollection?.(true);
              trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                originPage: AnalyticsPage.Library,
                originComponent: AnalyticsComponent.ActivityActionsMenu,
                buttonName: 'Add to a collection',
              });
            }}
            onClickDuplicateResource={duplicateActivity}
            onClickRenameResource={() => {
              setIsRenameActivityModalOpen?.(true);
              trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                originPage: AnalyticsPage.Library,
                originComponent: AnalyticsComponent.ActivityActionsMenu,
                buttonName: 'Rename',
              });
            }}
            onClickModifyFileSettings={() => {
              setIsFileSettingsModalOpen?.(true);
              trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                originPage: AnalyticsPage.Library,
                originComponent: AnalyticsComponent.ActivityActionsMenu,
                buttonName: 'Modify file settings',
              });
            }}
            onClickArchiveResource={() => {
              setIsConfirmArchiveActivityModalOpen?.(true);
              trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                originPage: AnalyticsPage.Library,
                originComponent: AnalyticsComponent.ActivityActionsMenu,
                buttonName: 'Archive resource',
              });
            }}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default LibraryTable;
